import { IonButton, IonItem, IonLabel, IonList } from '@ionic/react';
import React, { Component } from 'react';
import _ from 'underscore';

import { Log } from '@biteinc/common';
import { TimeHelper } from '@biteinc/core-react';
import { CardSchemeId, Gateway, IntegrationSystem } from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import OrderHelper from '~/app/js/gcn_order_helper';
import { localizeStr } from '~/app/js/localization/localization';
import LocalStorage from '~/app/js/utils/local_storage';
import { GCNAlertView } from '~/app/js/views/gcn_alert_view';
import { LocationUtils } from '~/helpers';
import GcnGiftCardHelper from '~/helpers/gcn_gift_card_helper';
import StoredValueHelper from '~/helpers/stored_value_helper';
import { AuthService } from '~/services';

import GcnHtml from '../app/js/gcn_html';
import type { GcnTransaction, KioskLocation } from '../types';
import type { StoredValueCard, StoredValueCardWithBalance } from '../types/stored_value_card';
import type { FreedomPayEcommUtils } from './ecomm-payment/freedom-pay/freedom-pay-ecomm-form.utils';

let partialTenderTimeout: NodeJS.Timeout | null = null;

interface StoredValueProps {
  transactions: GcnTransaction[];
  submitOrder: (queuePath?: string) => void;
  location: KioskLocation;
  savedCards?: FreedomPayEcommUtils.SavedCard[];
}

type StoredValueAuthOption = Readonly<{
  allowMultipleAccounts: boolean;
  authUrl: string;
  buttonText: string;
  cardSchemeId?: CardSchemeId;
  key: string;
}>;

type StoredValueMultipleAuthIntegration = Readonly<{
  allowMultipleAccountsForPrimaryAuthUrl?: boolean;
  allowMultipleAccountsForSecondaryAuthUrl?: boolean;
  secondaryExternalSsoAuthUrl?: string;
}>;

export default class StoredValuePanel extends Component<StoredValueProps> {
  componentWillUnmount(): void {
    this.cancelPartialTenderTimeout();
  }

  private requiresGiftCardPaymentToken(): boolean {
    const storedValueSystem = gcn.location.getStoredValueSystem();
    const hasExternalSsoAuthUrl = !!gcn.location.getStoredValueIntegration()?.externalSsoAuthUrl;

    // Atrium on Flash only works if a payment token is passed in or external SSO auth url is set
    const requiresPaymentToken =
      (storedValueSystem === IntegrationSystem.CbordCampusCard ||
        storedValueSystem === IntegrationSystem.CbordStoredValue ||
        storedValueSystem === IntegrationSystem.AtriumStoredValue) &&
      !hasExternalSsoAuthUrl;

    return requiresPaymentToken;
  }

  private getStoredValueMultipleAuthIntegration(): StoredValueMultipleAuthIntegration | undefined {
    const storedValueIntegration = gcn.location.getStoredValueIntegration();
    if (storedValueIntegration?.system !== IntegrationSystem.FreedomPayStoredValue) {
      return undefined;
    }
    return storedValueIntegration;
  }

  private getStoredValueAuthOptions(): StoredValueAuthOption[] {
    const storedValueIntegration = gcn.location.getStoredValueIntegration();
    const storedValueMultipleAuthIntegration = this.getStoredValueMultipleAuthIntegration();
    const primaryAuthUrl = storedValueIntegration?.externalSsoAuthUrl;
    if (!primaryAuthUrl) {
      return [];
    }

    if (gcn.location.get('giftCardI9n') !== IntegrationSystem.FreedomPayStoredValue) {
      return [
        {
          allowMultipleAccounts: false,
          authUrl: primaryAuthUrl,
          buttonText: localizeStr(Strings.LOGIN_GIFT_CARD),
          key: 'stored-value',
        },
      ];
    }

    const authOptions: StoredValueAuthOption[] = [
      {
        allowMultipleAccounts:
          !!storedValueMultipleAuthIntegration?.allowMultipleAccountsForPrimaryAuthUrl,
        authUrl: primaryAuthUrl,
        buttonText: localizeStr(Strings.LOGIN_GIFT_CARD),
        cardSchemeId: CardSchemeId.FreedomPayStoredValue,
        key: 'primary',
      },
    ];

    const secondaryAuthUrl = storedValueMultipleAuthIntegration?.secondaryExternalSsoAuthUrl;

    if (secondaryAuthUrl) {
      authOptions.push({
        allowMultipleAccounts:
          !!storedValueMultipleAuthIntegration?.allowMultipleAccountsForSecondaryAuthUrl,
        authUrl: secondaryAuthUrl,
        buttonText: localizeStr(Strings.LOGIN_GIFT_CARD_SECONDARY),
        cardSchemeId: CardSchemeId.FreedomPayVoucher,
        key: 'secondary',
      });
    }

    return authOptions;
  }

  private redirectToStoredValueSso(
    storedValueSystem: StoredValueHelper.StoredValueLikeSystem,
    authUrl: string,
    allowMultipleAccounts: boolean,
    cardSchemeId?: CardSchemeId,
  ): void {
    // Use the current URL as the redirect URL
    const url = new URL(window.location.href);
    url.search = ''; // Remove query parameters
    url.hash = ''; // Remove the checkout fragment
    const currentUrl = url.toString();
    // CBORD and Atrium use the same SSO flow, but Atrium requires a payment token be passed in
    // mobileapp=3 is required to indicate that after completing SSO authentication we redirect to a url
    // the location of the redirect is indicated with redirecturl
    LocalStorage.setCustomKeyItem('ssoContext', 'stored-value');
    const storedValueAuthUrl = allowMultipleAccounts
      ? StoredValueHelper.getMultipleAccountStoredValueSsoAuthUrl(storedValueSystem, authUrl)
      : authUrl;
    const loginUrl = StoredValueHelper.getStoredValueSsoUrl(
      storedValueSystem,
      storedValueAuthUrl,
      currentUrl,
    );

    if (storedValueSystem === IntegrationSystem.FreedomPayStoredValue && cardSchemeId) {
      LocalStorage.setItem(
        StoredValueHelper.freedomPayStoredValueCardSchemeIdStorageKey,
        String(cardSchemeId),
      );
    }

    gcn.menuView.showSpinner(localizeStr(Strings.LOADING));
    // Remove FreedomPay HPC iframes before redirecting; their scripts can block page unload.
    document.querySelectorAll('iframe[src*="freedompay"]').forEach((iframe) => {
      iframe.remove();
    });
    // Redirect to the SSO login page to get the payment token
    window.location.assign(loginUrl);
  }

  private startStoredValuePayment(authOption?: StoredValueAuthOption): void {
    const storedValueSystem = gcn.location.getStoredValueSystem();
    const storedValueIntegration = gcn.location.getStoredValueIntegration();
    const sessionStoredValueCards = gcn.orderManager.getStoredValueCards().filter((card) => {
      return !!card.authSessionToken;
    });
    const configuredAuthUrl = authOption?.authUrl ?? storedValueIntegration?.externalSsoAuthUrl;
    if (
      storedValueSystem &&
      (!sessionStoredValueCards.length || !!authOption) &&
      !this.requiresGiftCardPaymentToken() &&
      StoredValueHelper.storedValueSupportsSavedCards(storedValueSystem)
    ) {
      if (configuredAuthUrl) {
        this.redirectToStoredValueSso(
          storedValueSystem,
          configuredAuthUrl,
          !!authOption?.allowMultipleAccounts,
          authOption?.cardSchemeId,
        );
      } else {
        AuthService.showLogin(gcn.location.attributes, () => {}, true);
      }
    } else {
      const callback = (queuePath?: string): void => {
        Log.debug('gift card used');
        // If fully paid and the gift card method allows completion of payment, proceed to next
        // step.
        if (gcn.orderManager.getGrandTotal() === 0) {
          this.props.submitOrder(queuePath);
        }
        // On first tender, we start a timeout. If they do not finish payment in 2 minutes we warn
        // them. If they don't finish 5 minutes after the warning we refund them.
        this.startPartialTenderTimeout();
      };

      if (this.requiresGiftCardPaymentToken()) {
        // Payment by token requires an email because it's generally shared with eComm.
        GcnGiftCardHelper.ensureGuestEmail(() => {
          gcn.menuView.dismissModalPopup();
          // If we previously took payment and then failed to send the order, then we're
          // already settled up
          // TODO: when did this get closed though
          if (gcn.orderManager.getGrandTotal() === 0) {
            callback();
            return;
          }
          GcnGiftCardHelper.showGiftCardView('stored-value', callback);
        });
      } else {
        GcnGiftCardHelper.showGiftCardView('stored-value', callback);
      }
    }
  }

  private storedValueCardMatchesAuthOption(
    card: StoredValueCard,
    authOption: StoredValueAuthOption,
  ): boolean {
    if (gcn.location.get('giftCardI9n') === IntegrationSystem.FreedomPayStoredValue) {
      return (card.cardSchemeId ?? CardSchemeId.FreedomPayStoredValue) === authOption.cardSchemeId;
    }
    return authOption.key === 'stored-value';
  }

  private savedCardMatchesAuthOption(
    card: FreedomPayEcommUtils.SavedCard,
    authOption: StoredValueAuthOption,
  ): boolean {
    if (
      gcn.location.get('giftCardI9n') === IntegrationSystem.FreedomPayStoredValue &&
      card.gateway === Gateway.FreedomPayStoredValue
    ) {
      return card.cardSchemeId === authOption.cardSchemeId;
    }
    return authOption.key === 'stored-value';
  }

  private getStoredValueButton(buttonText: string): React.ReactNode {
    return (
      <IonItem key="use-stored-value">
        <IonButton
          className="use-stored-value-button"
          expand="block"
          fill="outline"
          size="default"
          onClick={() => {
            this.startStoredValuePayment();
          }}
        >
          {buttonText}
        </IonButton>
      </IonItem>
    );
  }

  private getStoredValueAuthOptionButtons(authOptions: StoredValueAuthOption[]): React.ReactNode[] {
    return authOptions.map((authOption) => {
      return (
        <IonItem key={authOption.key}>
          <IonButton
            className="use-stored-value-button"
            expand="block"
            fill="outline"
            size="default"
            onClick={() => {
              this.startStoredValuePayment(authOption);
            }}
          >
            {authOption.buttonText}
          </IonButton>
        </IonItem>
      );
    });
  }

  private getStoredValueButtons(): React.ReactNode {
    if (!LocationUtils.hasStoredValueIntegration(this.props.location)) {
      return;
    }
    if (gcn.orderManager.getGrandTotal() === 0) {
      return;
    }

    const authOptions = this.getStoredValueAuthOptions();
    const sessionStoredValueCards = gcn.orderManager.getStoredValueCards().filter((card) => {
      return !!card.authSessionToken;
    });
    const freedomPaySavedCards =
      this.props.savedCards?.filter((card) => {
        return card.gateway === Gateway.FreedomPayStoredValue;
      }) ?? [];
    const requiresPaymentToken = this.requiresGiftCardPaymentToken();
    const visibleAuthOptions = authOptions.filter((authOption) => {
      const hasLinkedStoredValueAccountForAuthOption =
        sessionStoredValueCards.some((card) => {
          return this.storedValueCardMatchesAuthOption(card, authOption);
        }) ||
        freedomPaySavedCards.some((card) => {
          return this.savedCardMatchesAuthOption(card, authOption);
        });
      return !hasLinkedStoredValueAccountForAuthOption || authOption.allowMultipleAccounts;
    });
    const canShowAuthOptions = visibleAuthOptions.length > 0;
    const hasUnusedStoredValueCards = !!gcn.orderManager.getUnusedStoredValueCards().length;
    const canShowStoredValueCardButton =
      sessionStoredValueCards.length && hasUnusedStoredValueCards;

    if (sessionStoredValueCards.length && !hasUnusedStoredValueCards && !canShowAuthOptions) {
      // hide button if stored value session token cards all used
      return;
    }
    // Atrium on Flash only works if a payment token is passed in.
    if (requiresPaymentToken && !sessionStoredValueCards.length) {
      return;
    }

    // FP SV uses the ecomm form's saved-card UX — once a FP SV card is in the profile or
    // mocked from localStorage, hide this button to avoid routing into the card+PIN flow.
    const shouldHideStoredValueCardButton =
      gcn.location.get('giftCardI9n') === IntegrationSystem.FreedomPayStoredValue &&
      freedomPaySavedCards.length &&
      !canShowAuthOptions;
    if (shouldHideStoredValueCardButton) {
      return;
    }

    if (
      gcn.orderManager.getIsStoredValueAuthed() &&
      !sessionStoredValueCards.length &&
      !canShowAuthOptions
    ) {
      return;
    }

    const storedValueButtonText = !sessionStoredValueCards.length
      ? localizeStr(Strings.LOGIN_GIFT_CARD)
      : localizeStr(Strings.USE_GIFT_CARD);

    if (canShowAuthOptions) {
      return [
        ...(canShowStoredValueCardButton ? [this.getStoredValueButton(storedValueButtonText)] : []),
        ...this.getStoredValueAuthOptionButtons(visibleAuthOptions),
      ];
    }

    return this.getStoredValueButton(storedValueButtonText);
  }

  private static refundTransaction(transaction: GcnTransaction): void {
    gcn.menuView.showSpinner(localizeStr(Strings.REFUNDING));
    gcn.orderManager.refundStoredValueTransaction(transaction, () => {
      gcn.menuView.dismissSpinner();
    });
  }

  // If another payment is made, this will be cancelled
  private cancelPartialTenderTimeout = (): void => {
    // Check if existing timer. Starting a new one should clear an old one.
    if (partialTenderTimeout) {
      clearTimeout(partialTenderTimeout);
      partialTenderTimeout = null;
    }
  };

  private startPartialTenderTimeout = (): void => {
    this.cancelPartialTenderTimeout();
    if (gcn.orderManager.getTransactions().length === 0) {
      return;
    }
    // Pop-up will show up after 2 minutes of no additional transactions.
    partialTenderTimeout = setTimeout(() => {
      const alertText = localizeStr(Strings.PARTIAL_TENDER_TIMEOUT, [], function (string: string) {
        return string.split('\n').join('<br />');
      });
      const warningView = new GCNAlertView({
        text: alertText,
        okCallback() {
          gcn.menuView.dismissModalPopup();
        },
      });
      gcn.menuView.showModalPopup(warningView);
    }, 2 * TimeHelper.MINUTE);
  };

  private static getRemainingBalance(card: StoredValueCardWithBalance): React.ReactNode {
    if (_.isUndefined(card.remainingBalance)) {
      return undefined;
    }
    return (
      <p className="card-balance">
        {`${GcnHtml.stringFromPrice(card.remainingBalance)}`}
        {localizeStr(Strings.STORED_VALUE_REMAINING_BALANCE)}
      </p>
    );
  }

  private getTransactions(): React.ReactNode[] {
    const cardsByLastFour = gcn.orderManager
      .getStoredValueCardsAndRemainingBalance()
      .reduce(
        (
          storedValueCardsByLastFour: Record<string, StoredValueCardWithBalance>,
          card: StoredValueCardWithBalance,
        ) => {
          // only 1 card per last four
          // edge case here where we show the wrong card because last 4 is not unique
          storedValueCardsByLastFour[card.lastFour] = card;
          return storedValueCardsByLastFour;
        },
        {},
      );

    const nodes: React.ReactNode[] = [];
    if (this.props.transactions.length) {
      nodes.push(
        <IonItem className="stored-value-title">
          <IonLabel>
            <h3 className="title">{localizeStr(Strings.APPLIED_STORED_VALUE_CARDS)}</h3>
          </IonLabel>
        </IonItem>,
      );
      this.props.transactions.forEach((transaction) => {
        const card = cardsByLastFour[transaction.getLastFour()];
        const cardName = OrderHelper.getCardName(transaction);
        const cardLastFour = card?.lastFour ? ` (*${card.lastFour})` : '';
        nodes.push(
          <IonItem className="stored-value-transaction">
            <IonLabel slot="start">
              <h3 className="card-name">{`${cardName} ${cardLastFour}`}</h3>
              {StoredValuePanel.getRemainingBalance(card)}
            </IonLabel>
            <IonButton
              slot="end"
              fill="clear"
              className="refund-stored-value-transaction"
              onClick={() => {
                StoredValuePanel.refundTransaction(transaction);
                this.startPartialTenderTimeout();
              }}
            >
              {localizeStr(Strings.REMOVE_GIFT_CARD)}
            </IonButton>
          </IonItem>,
        );
      });
    }
    return nodes;
  }

  render(): React.ReactNode {
    return (
      <IonList
        lines="none"
        className={`stored-value ${this.props.transactions.length ? 'has-transactions' : ''}`}
      >
        {this.getTransactions()}
        {this.getStoredValueButtons()}
      </IonList>
    );
  }
}
